import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { RecordingDetail } from '@/components/recordings/RecordingDetail'
import { RescheduleModal } from '@/components/recordings/RescheduleModal'
import { AddGuestToEpisodeModal } from '@/components/episodes/AddGuestToEpisodeModal'
import { useSendReminder } from '@/hooks/useInvitations'
import type { RecordingDetail as RecordingDetailType, PrepChecklist } from '@/types'
import { useLabels, singular } from '@/lib/terminology'

// Rate limit: 5 minutes between reminders per guest
const REMINDER_COOLDOWN_MS = 5 * 60 * 1000

interface EpisodeData {
  id: string
  number: number
  title: string
  description: string | null
  recording_date: string
  recording_time: string | null
  air_date: string
  status: string
  platform: string | null
  meeting_link: string | null
  duration: string | null
  notes: string | null
  series_id: string
}

interface AppearanceData {
  id: string
  stage: string
  guests: {
    id: string
    name: string
    title: string
    company: string | null
    avatar_url: string | null
    email: string | null
  }
}

interface PrepChecklistData {
  id: string
  appearance_id: string
  research_complete: boolean
  questions_ready: boolean
  guest_briefed: boolean
  equipment_tested: boolean
  updated_at: string
}

export function RecordingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const supabase = createClient()
  const queryClient = useQueryClient()
  const sendReminder = useSendReminder()
  const labels = useLabels()
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  const [showAddGuestModal, setShowAddGuestModal] = useState(false)
  const [sendingReminderIds, setSendingReminderIds] = useState<Set<string>>(new Set())
  const [reminderSentIds, setReminderSentIds] = useState<Set<string>>(new Set())
  const [isRemindingAll, setIsRemindingAll] = useState(false)
  const [reminderError, setReminderError] = useState<string | null>(null)
  const reminderTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())

  // Cleanup timeouts on unmount
  useEffect(() => {
    const timeouts = reminderTimeoutsRef.current
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout))
    }
  }, [])

  // Fetch episode/recording data
  const { data: episode, isLoading: episodeLoading } = useQuery({
    queryKey: ['recording', id],
    queryFn: async () => {
      if (!id) return null

      const { data, error } = await supabase
        .from('episodes')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data as EpisodeData
    },
    enabled: !!id,
  })

  // Fetch appearances/guests for this episode
  const { data: appearances = [] } = useQuery({
    queryKey: ['recording-appearances', id],
    queryFn: async () => {
      if (!id) return []

      const { data, error } = await supabase
        .from('appearances')
        .select(`
          id,
          stage,
          ready_to_record,
          guests (
            id,
            name,
            title,
            company,
            avatar_url,
            email
          )
        `)
        .eq('episode_id', id)

      if (error) throw error
      return (data as (AppearanceData & { ready_to_record: boolean })[]) || []
    },
    enabled: !!id,
  })

  // Fetch prep checklist for the first appearance
  const primaryAppearanceId = appearances[0]?.id
  const { data: prepChecklist } = useQuery({
    queryKey: ['prep-checklist', primaryAppearanceId],
    queryFn: async (): Promise<PrepChecklistData | null> => {
      if (!primaryAppearanceId) return null

      const { data, error } = await supabase
        .from('prep_checklists')
        .select('*')
        .eq('appearance_id', primaryAppearanceId)
        .single()

      if (error && error.code !== 'PGRST116') throw error
      return data as PrepChecklistData | null
    },
    enabled: !!primaryAppearanceId,
  })

  // Fetch last reminder sent times for rate limiting
  const guestIds = appearances.map(a => a.guests?.id).filter(Boolean) as string[]
  const { data: lastReminderData } = useQuery({
    queryKey: ['last-reminders', id, guestIds],
    queryFn: async (): Promise<Record<string, string>> => {
      if (guestIds.length === 0) return {}

      // Get the most recent reminder email for each guest
      const { data, error } = await supabase
        .from('communications')
        .select('guest_id, created_at')
        .in('guest_id', guestIds)
        .eq('type', 'email')
        .eq('direction', 'outbound')
        .order('created_at', { ascending: false })

      if (error) throw error

      // Build a map of guest_id -> last reminder timestamp
      const lastReminderMap: Record<string, string> = {}
      const communications = data as { guest_id: string; created_at: string }[] | null
      for (const comm of communications || []) {
        if (!lastReminderMap[comm.guest_id]) {
          lastReminderMap[comm.guest_id] = comm.created_at
        }
      }
      return lastReminderMap
    },
    enabled: guestIds.length > 0,
    refetchInterval: 30000, // Refetch every 30 seconds to update countdown
  })

  // Calculate which guests are on cooldown - use state to avoid impure function in render
  const [guestCooldowns, setGuestCooldowns] = useState<Record<string, number>>({})

  useEffect(() => {
    const calculateCooldowns = () => {
      const cooldowns: Record<string, number> = {}
      const now = Date.now()

      for (const [guestId, timestamp] of Object.entries(lastReminderData || {})) {
        const sentAt = new Date(timestamp).getTime()
        const elapsed = now - sentAt
        if (elapsed < REMINDER_COOLDOWN_MS) {
          cooldowns[guestId] = Math.ceil((REMINDER_COOLDOWN_MS - elapsed) / 60000) // minutes remaining
        }
      }
      setGuestCooldowns(cooldowns)
    }

    calculateCooldowns()

    // Update countdown every minute
    const interval = setInterval(calculateCooldowns, 60000)
    return () => clearInterval(interval)
  }, [lastReminderData])

  // Upsert prep checklist mutation
  const updatePrepChecklist = useMutation({
    mutationFn: async ({
      field,
      value,
    }: {
      field: keyof PrepChecklist
      value: boolean
    }) => {
      if (!primaryAppearanceId) return

      // Check if a prep checklist record already exists
      const { data: existing } = await supabase
        .from('prep_checklists')
        .select('id')
        .eq('appearance_id', primaryAppearanceId)
        .maybeSingle()

      const fieldMap: Record<keyof PrepChecklist, string> = {
        researchComplete: 'research_complete',
        questionsReady: 'questions_ready',
        guestBriefed: 'guest_briefed',
        equipmentTested: 'equipment_tested',
      }

      const dbField = fieldMap[field]

      if (existing) {
        // Update only the specified field on existing record
        const { error } = await supabase
          .from('prep_checklists')
          .update({ [dbField]: value } as never)
          .eq('appearance_id', primaryAppearanceId)

        if (error) throw error
      } else {
        // Insert new record with all fields explicitly set to defaults
        const { error } = await supabase
          .from('prep_checklists')
          .insert({
            appearance_id: primaryAppearanceId,
            research_complete: field === 'researchComplete' ? value : false,
            questions_ready: field === 'questionsReady' ? value : false,
            guest_briefed: field === 'guestBriefed' ? value : false,
            equipment_tested: field === 'equipmentTested' ? value : false,
          } as never)

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prep-checklist', primaryAppearanceId] })
    },
  })

  // Update episode notes
  const updateNotes = useMutation({
    mutationFn: async (note: string) => {
      if (!id || !episode) return

      const currentNotes = episode.notes || ''
      const newNotes = currentNotes ? `${currentNotes}\n\n${note}` : note

      const { error } = await supabase
        .from('episodes')
        .update({ notes: newNotes } as never)
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recording', id] })
    },
  })

  // Reschedule episode mutation
  const rescheduleEpisode = useMutation({
    mutationFn: async ({
      recordingDate,
      recordingTime,
    }: {
      recordingDate: string
      recordingTime?: string
    }) => {
      if (!id) return

      const { error } = await supabase
        .from('episodes')
        .update({
          recording_date: recordingDate,
          recording_time: recordingTime || null,
        } as never)
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recording', id] })
      queryClient.invalidateQueries({ queryKey: ['recordings'] })
      setShowRescheduleModal(false)
    },
  })

  // Mark ready mutation - updates all appearances to ready_to_record and advances stage to 'prep'
  const markReady = useMutation({
    mutationFn: async () => {
      if (!id || appearances.length === 0) return

      const appearanceIds = appearances.map((a) => a.id)

      const { error } = await supabase
        .from('appearances')
        .update({
          ready_to_record: true,
          stage: 'prep',
        } as never)
        .in('id', appearanceIds)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recording-appearances', id] })
      queryClient.invalidateQueries({ queryKey: ['recordings'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline'] })
    },
  })

  if (episodeLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-6 w-32 bg-zinc-200 dark:bg-zinc-800 rounded mb-4" />
          <div className="h-10 w-96 bg-zinc-200 dark:bg-zinc-800 rounded mb-2" />
          <div className="h-4 w-64 bg-zinc-200 dark:bg-zinc-800 rounded" />
        </div>
      </div>
    )
  }

  if (!episode) {
    return (
      <div className="p-6 text-center">
        <p className="text-zinc-500 dark:text-zinc-400">{singular(labels.recordings)} not found</p>
        <button
          onClick={() => navigate('/recordings')}
          className="mt-4 text-primary-500 hover:text-primary-600"
        >
          Back to {labels.recordings}
        </button>
      </div>
    )
  }

  // Get the overall stage from appearances (use the "furthest" stage)
  const stageOrder = ['invited', 'responded', 'prep', 'recorded', 'published']
  const overallStage = appearances.reduce((maxStage, app) => {
    const currentIndex = stageOrder.indexOf(app.stage)
    const maxIndex = stageOrder.indexOf(maxStage)
    return currentIndex > maxIndex ? app.stage : maxStage
  }, appearances[0]?.stage || 'invited') as RecordingDetailType['episode']['stage']

  // Transform data for RecordingDetail component
  const recordingData: RecordingDetailType = {
    episode: {
      id: episode.id,
      number: episode.number,
      title: episode.title,
      description: episode.description || undefined,
      recordingDate: episode.recording_date,
      recordingTime: episode.recording_time || undefined,
      airDate: episode.air_date,
      stage: overallStage,
      platform: episode.platform || undefined,
      meetingLink: episode.meeting_link || undefined,
      duration: episode.duration || undefined,
    },
    guests: appearances
      .filter((a) => a.guests)
      .map((a) => ({
        id: a.guests.id,
        name: a.guests.name,
        title: a.guests.title,
        company: a.guests.company || undefined,
        avatarUrl: a.guests.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(a.guests.name)}&background=6369D1&color=fff`,
      })),
    prep: {
      researchComplete: prepChecklist?.research_complete ?? false,
      questionsReady: prepChecklist?.questions_ready ?? false,
      guestBriefed: prepChecklist?.guest_briefed ?? false,
      equipmentTested: prepChecklist?.equipment_tested ?? false,
    },
    activity: [],
    notes: episode.notes || undefined,
  }

  return (
    <div className="p-6">
      <RecordingDetail
        recording={recordingData}
        onBack={() => navigate('/recordings')}
        onPrepToggle={(field: keyof PrepChecklist) => {
          const currentValue = recordingData.prep[field]
          updatePrepChecklist.mutate({ field, value: !currentValue })
        }}
        onSendReminder={(guestId: string) => {
          // Check cooldown
          if (guestCooldowns[guestId]) {
            setReminderError(`Please wait ${guestCooldowns[guestId]} more minute(s) before sending another reminder.`)
            return
          }

          // Find the appearance for this guest
          const appearance = appearances.find((a) => a.guests?.id === guestId)
          if (appearance) {
            setReminderError(null)
            setSendingReminderIds((prev) => new Set(prev).add(guestId))
            sendReminder.mutate(
              {
                appearance_id: appearance.id,
                guest_id: guestId,
              },
              {
                onSuccess: () => {
                  setSendingReminderIds((prev) => {
                    const next = new Set(prev)
                    next.delete(guestId)
                    return next
                  })
                  setReminderSentIds((prev) => new Set(prev).add(guestId))
                  // Invalidate last reminders to update cooldown
                  queryClient.invalidateQueries({ queryKey: ['last-reminders', id] })
                  // Clear any existing timeout for this guest
                  const existingTimeout = reminderTimeoutsRef.current.get(guestId)
                  if (existingTimeout) clearTimeout(existingTimeout)
                  // Set new timeout to clear success state
                  const timeout = setTimeout(() => {
                    setReminderSentIds((prev) => {
                      const next = new Set(prev)
                      next.delete(guestId)
                      return next
                    })
                    reminderTimeoutsRef.current.delete(guestId)
                  }, 3000)
                  reminderTimeoutsRef.current.set(guestId, timeout)
                },
                onError: (error) => {
                  setSendingReminderIds((prev) => {
                    const next = new Set(prev)
                    next.delete(guestId)
                    return next
                  })
                  setReminderError(error instanceof Error ? error.message : 'Failed to send reminder')
                },
              }
            )
          }
        }}
        onRemindAll={() => {
          setIsRemindingAll(true)
          setReminderError(null)
          // Send reminder to all guests who are not on cooldown
          const eligibleAppearances = appearances
            .filter((a) => a.guests && !guestCooldowns[a.guests.id])

          if (eligibleAppearances.length === 0) {
            setReminderError('All guests are on cooldown. Please wait before sending reminders.')
            setIsRemindingAll(false)
            return
          }

          const reminderPromises = eligibleAppearances.map((appearance) =>
            sendReminder.mutateAsync({
              appearance_id: appearance.id,
              guest_id: appearance.guests.id,
            })
          )
          Promise.allSettled(reminderPromises).then((results) => {
            const successfulGuestIds = results
              .map((result, index) =>
                result.status === 'fulfilled' ? eligibleAppearances[index]?.guests?.id : null
              )
              .filter((id): id is string => Boolean(id))

            if (successfulGuestIds.length > 0) {
              setReminderSentIds((prev) => {
                const next = new Set(prev)
                successfulGuestIds.forEach((id) => next.add(id))
                return next
              })
            }

            queryClient.invalidateQueries({ queryKey: ['last-reminders', id] })
          }).finally(() => {
            setIsRemindingAll(false)
          })
        }}
        onReschedule={() => setShowRescheduleModal(true)}
        onMarkReady={() => markReady.mutate()}
        isAlreadyReady={appearances.length === 0 || appearances.every((a) => a.ready_to_record)}
        isMarkingReady={markReady.isPending}
        onAddNote={(note: string) => {
          updateNotes.mutate(note)
        }}
        onAddGuest={() => setShowAddGuestModal(true)}
        sendingReminderIds={sendingReminderIds}
        reminderSentIds={reminderSentIds}
        isRemindingAll={isRemindingAll}
        guestCooldowns={guestCooldowns}
        reminderError={reminderError}
        onDismissError={() => setReminderError(null)}
      />

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={showRescheduleModal}
        onClose={() => setShowRescheduleModal(false)}
        currentDate={episode.recording_date}
        currentTime={episode.recording_time || undefined}
        onReschedule={(date, time) => rescheduleEpisode.mutate({ recordingDate: date, recordingTime: time })}
        isPending={rescheduleEpisode.isPending}
      />

      {/* Add Guest Modal */}
      <AddGuestToEpisodeModal
        isOpen={showAddGuestModal}
        onClose={() => setShowAddGuestModal(false)}
        episodeId={episode.id}
        episodeTitle={episode.title}
        currentRecordingDate={episode.recording_date}
        currentRecordingTime={episode.recording_time || undefined}
        currentMeetingLink={episode.meeting_link || undefined}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['recording-appearances', id] })
          queryClient.invalidateQueries({ queryKey: ['recording', id] })
        }}
      />
    </div>
  )
}
